import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { authService } from '../../services/authService';
import './Auth.css';

interface RequestFormData {
  email: string;
}

interface ResetFormData {
  code: string;
  newPassword: string;
  confirmPassword: string;
}

export const ForgotPassword: React.FC = () => {
  const [step, setStep] = useState<'request' | 'reset' | 'success'>('request');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestForm = useForm<RequestFormData>();
  const resetForm = useForm<ResetFormData>();

  const onRequestSubmit = async (data: RequestFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      await authService.requestPasswordReset(data.email);
      setEmail(data.email);
      setStep('reset');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send reset code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const onResetSubmit = async (data: ResetFormData) => {
    if (data.newPassword !== data.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await authService.resetPassword(email, data.code, data.newPassword);
      setStep('success');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'success') {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2>Password Reset Complete</h2>
          <div className="success-message">
            <p>Your password has been successfully reset.</p>
            <p>You can now log in with your new password.</p>
          </div>
          <div className="auth-footer">
            <Link to="/login" className="btn-primary">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'reset') {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2>Enter Reset Code</h2>
          <p className="subtitle">
            We sent a 6-digit code to <strong>{email}</strong>. Enter it below along with your new password.
          </p>

          {error && <div className="error-alert">{error}</div>}

          <form onSubmit={resetForm.handleSubmit(onResetSubmit)}>
            <div className="form-group">
              <label htmlFor="code">Reset Code</label>
              <input
                type="text"
                id="code"
                {...resetForm.register('code', {
                  required: 'Reset code is required',
                  pattern: {
                    value: /^\d{6}$/,
                    message: 'Code must be 6 digits',
                  },
                })}
                className={resetForm.formState.errors.code ? 'error' : ''}
                placeholder="123456"
                maxLength={6}
                autoComplete="one-time-code"
                inputMode="numeric"
                autoFocus
              />
              {resetForm.formState.errors.code && (
                <span className="error-message">{resetForm.formState.errors.code.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                autoComplete="new-password"
                {...resetForm.register('newPassword', {
                  required: 'New password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters',
                  },
                  pattern: {
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
                    message: 'Must include uppercase, lowercase, number, and special character',
                  },
                })}
                className={resetForm.formState.errors.newPassword ? 'error' : ''}
                placeholder="New password"
              />
              {resetForm.formState.errors.newPassword && (
                <span className="error-message">{resetForm.formState.errors.newPassword.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                autoComplete="new-password"
                {...resetForm.register('confirmPassword', {
                  required: 'Please confirm your password',
                })}
                className={resetForm.formState.errors.confirmPassword ? 'error' : ''}
                placeholder="Confirm new password"
              />
              {resetForm.formState.errors.confirmPassword && (
                <span className="error-message">{resetForm.formState.errors.confirmPassword.message}</span>
              )}
            </div>

            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>

          <div className="auth-footer">
            <p>
              Didn't receive a code?{' '}
              <button
                type="button"
                className="link"
                onClick={() => { setStep('request'); setError(null); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                Try again
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>Reset Your Password</h2>
        <p className="subtitle">
          Enter your email address and we'll send you a code to reset your password.
        </p>

        {error && <div className="error-alert">{error}</div>}

        <form onSubmit={requestForm.handleSubmit(onRequestSubmit)}>
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              {...requestForm.register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              className={requestForm.formState.errors.email ? 'error' : ''}
              placeholder="you@example.com"
              autoFocus
            />
            {requestForm.formState.errors.email && (
              <span className="error-message">{requestForm.formState.errors.email.message}</span>
            )}
          </div>

          <button type="submit" className="btn-primary" disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Send Reset Code'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Remember your password?{' '}
            <Link to="/login" className="link">
              Back to Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
